/**
 * ---------------------------------------------------------------------------------------------------------------------
 * IL-DISPLAY-STORES — 商品頁「展示門市」（新增檔案）
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * 由 snippets/il-display-stores.liquid 載入，並從該 snippet 內嵌的兩份 JSON 取得資料：
 *   #il-display-stores-data          15 筆門市的完整資料（SSR，Liquid 渲染）
 *   #il-display-stores-product-keys  當前商品的 variant SKU 清單與 handle
 *
 * 比對鏈：
 *   SKU 前綴（= Set Code）→ CSV 篩出對應的 Display name → 與內嵌門市資料交集 → 渲染
 *
 * 為什麼用 SKU 而不是 handle：
 *   SKU 格式為 {Set Code}-{顏色碼}（例：ILB0008A-L851L），全站 1132 個有填 SKU 的 variant 都符合。
 *   而 handle 是從商品標題衍生的，全站 284 筆商品只有 9% 剛好等於貨號。handle 僅作備援。
 */

(() => {
  'use strict';

  const FETCH_TIMEOUT_MS = 8000;

  /**
   * 快取級距。CSV 網址加上時間戳記以避開瀏覽器與 CDN 快取，但用 5 分鐘級距而非
   * 毫秒，讓同一使用者連續瀏覽多個商品頁時能命中快取，同時保證 5 分鐘內看到新內容。
   */
  const CACHE_BUCKET_MS = 5 * 60 * 1000;

  /** 客戶端 CSV 欄位命名可能不同，以下為可接受的別名（皆轉小寫比對）。 */
  const SET_CODE_HEADERS = ['set code', 'setcode', 'set_code'];
  const DISPLAY_NAME_HEADERS = ['display name', 'displayname', 'display_name'];

  const root = document.querySelector('[data-il-display-stores]');
  if (!root) return;

  const csvUrl = root.dataset.csvUrl;
  if (!csvUrl) return;

  const listEl = root.querySelector('[data-il-display-stores-list]');
  const ctaEl = root.querySelector('.il-displayStores__cta');
  const messageEl = root.querySelector('.il-displayStores__message');
  if (!listEl || !ctaEl || !messageEl) return;

  const stores = readJson('il-display-stores-data');
  const productKeys = readJson('il-display-stores-product-keys');
  if (!Array.isArray(stores) || stores.length === 0 || !productKeys) return;

  const setCodes = productSetCodes(productKeys);
  if (setCodes.size === 0) return;

  /**
   * 門市名稱正規化：轉小寫後把所有非英數字元收斂成單一連字號。
   * 客戶 CSV 的分隔符並不一致（FED_Taichung 用底線、FED A13_Xinyi 混空格與底線、
   * Tai Mall 用空格），正規化後兩側才能穩定對上。
   */
  function normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function readJson(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /** 由 variant SKU 前綴推出 Set Code 集合，並把 handle 一併納入作為備援。 */
  function productSetCodes(keys) {
    const codes = new Set();

    (keys.skus || []).forEach((sku) => {
      if (!sku) return;
      const code = String(sku).trim().split('-')[0].toLowerCase();
      if (code) codes.add(code);
    });

    if (keys.handle) codes.add(String(keys.handle).trim().toLowerCase());

    return codes;
  }

  function splitRow(line) {
    return line.split(',').map((cell) => cell.trim().replace(/^"(.*)"$/, '$1'));
  }

  /** 以欄位名稱（header）對應，不用欄位順序，客戶調整欄位順序時不受影響。 */
  function parseCsv(text) {
    const lines = text
      .replace(/^\uFEFF/, '')
      .split(/\r\n|\n|\r/)
      .filter((line) => line.trim() !== '');

    if (lines.length < 2) return { headers: [], rows: [] };

    const headers = splitRow(lines[0]).map((header) => header.toLowerCase());
    const rows = lines.slice(1).map((line) => {
      const cells = splitRow(line);
      const row = {};
      headers.forEach((header, index) => {
        row[header] = cells[index] || '';
      });
      return row;
    });

    return { headers, rows };
  }

  function findHeader(headers, candidates) {
    return candidates.find((candidate) => headers.includes(candidate));
  }

  function matchStores(csvText) {
    const { headers, rows } = parseCsv(csvText);
    const setCodeHeader = findHeader(headers, SET_CODE_HEADERS);
    const displayNameHeader = findHeader(headers, DISPLAY_NAME_HEADERS);

    if (!setCodeHeader || !displayNameHeader) {
      throw new Error(`CSV 缺少必要欄位，實際欄位為：${headers.join(', ')}`);
    }

    // 同一個 Set Code 會因不同顏色或不同門市重複出現多列，這裡直接以 Set 去重。
    const wanted = new Set();
    rows.forEach((row) => {
      const code = row[setCodeHeader].trim().toLowerCase();
      if (!code || !setCodes.has(code)) return;

      const displayName = normalize(row[displayNameHeader]);
      if (displayName) wanted.add(displayName);
    });

    return stores.filter((store) => wanted.has(normalize(store.key)));
  }

  function fetchCsv(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const separator = url.includes('?') ? '&' : '?';
    const bucket = Math.floor(Date.now() / CACHE_BUCKET_MS);

    return fetch(`${url}${separator}t=${bucket}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .finally(() => clearTimeout(timer));
  }

  /** Icon 由 Liquid 預先渲染在 <template> 內，見 snippets/il-display-stores.liquid。 */
  function cloneIcon(name) {
    const template = document.querySelector(`[data-il-icon="${name}"]`);
    return template && template.content ? template.content.cloneNode(true) : null;
  }

  /**
   * 組出「icon + 文字」，並回傳文字節點讓呼叫端能單獨加 class。
   * 文字一定要包在 span 裡：直接對外層設 textContent 會把 icon 清掉，
   * 而主題的 .link 底線是用 background 畫的，掛在外層會連 icon 一起畫過去。
   */
  function appendWithIcon(element, iconName, text) {
    const icon = cloneIcon(iconName);
    if (icon) element.appendChild(icon);

    const label = document.createElement('span');
    label.textContent = text;
    element.appendChild(label);

    return label;
  }

  /**
   * 地址要連到哪裡：優先用後台「Store Map File URL」欄位的值（Google Maps 短網址），
   * 取不到時退回用經緯度自組連結，兩者都沒有就不做成連結。
   *
   * 只接受 http/https 開頭的值，有兩個理由：
   *   1. 這個值直接進 href，擋掉 javascript: 之類的協定。
   *   2. 若後台誤填成純文字地址，會退回經緯度而不是產生一個點不開的死連結。
   */
  function storeMapUrl(store) {
    const url = String(store.mapUrl == null ? '' : store.mapUrl).trim();
    if (/^https?:\/\//i.test(url)) return url;

    if (store.lat && store.lng) {
      const query = `${encodeURIComponent(store.lat)},${encodeURIComponent(store.lng)}`;
      return `https://www.google.com/maps?q=${query}`;
    }

    return '';
  }

  function renderStores(matched) {
    const lineLabel = root.dataset.lineLabel || 'LINE';
    const fragment = document.createDocumentFragment();

    matched.forEach((store) => {
      const item = document.createElement('div');
      item.className = 'il-displayStores__store';

      const name = document.createElement('p');
      name.className = 'il-displayStores__storeName bold';
      appendWithIcon(name, 'store', store.name || store.key);
      item.appendChild(name);

      if (store.address) {
        const mapUrl = storeMapUrl(store);
        const address = document.createElement(mapUrl ? 'a' : 'p');
        address.className = 'il-displayStores__storeAddress';
        appendWithIcon(address, 'pin', store.address);
        if (mapUrl) {
          address.href = mapUrl;
          address.target = '_blank';
          address.rel = 'noopener';
        }
        item.appendChild(address);
      }

      if (store.lineId) {
        const line = document.createElement('a');
        line.className = 'il-displayStores__storeLine button button--sm';
        // 後台的 LINE ID 帶 @ 前綴（例：@123pkiqm），必須編碼成 %40 才能組出正確網址。
        line.href = `https://line.me/R/ti/p/${encodeURIComponent(store.lineId)}`;
        line.target = '_blank';
        line.rel = 'noopener';
        appendWithIcon(line, 'line', lineLabel);
        item.appendChild(line);
      }

      fragment.appendChild(item);
    });

    listEl.replaceChildren(fragment);
  }

  function start() {
    fetchCsv(csvUrl)
      .then((csvText) => {
        const matched = matchStores(csvText);

        // 本商品沒有任何展示門市：CTA 整個不顯示。
        if (matched.length === 0) return;

        renderStores(matched);
        ctaEl.hidden = false;
      })
      .catch((error) => {
        // 讀取或解析失敗時不可留白，顯示提示文字讓顧客知道這裡有內容但尚未載入。
        messageEl.hidden = false;
        console.warn('[il-display-stores] 展示門市資料載入失敗：', error);
      });
  }

  // 延後到瀏覽器空閒才抓，避免與商品圖、GTM 等資源競爭頻寬。
  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 2000 });
  } else {
    setTimeout(start, 200);
  }
})();